(ns app.front.view.dispatch.email
  "Dispatching notification email that is sent to selected parties plus additional emails if given.
  Additional notes can be included, by default they contain the booking notes."
  (:require [app.domain.business.model :as business-model]
            [app.domain.party-role.model :as role-model]
            [app.front.ui :as ui]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :as futil]
            [app.front.view.dispatch.subs :as dispatch-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [goog.functions]
            [re-frame.core-instrumented :as rf]
            [re-frame.db]
            [schema.core :as s]))


(s/defn ^:private party-recipient-label :- s/Str
  [lang, recipient :- dispatch-subs/PartyEmailRecipient]
  (or (:email recipient)
      (business-model/business-name (:business recipient) lang)))


(s/defn ^:private format-party-recipient-roles :- s/Str
  [{:keys [lang tr]}, {:keys [roles requester?]} :- dispatch-subs/PartyEmailRecipient]
  (->> roles
       (map #(role-model/party-role-name % lang))
       sort
       (concat (when requester? [(tr [:role/requester])]))
       (str/join " ")))


(s/defn ^:private party-recipient
  [{:keys [lang] :as ctx}, mail-recipients
   {:keys [party-entity-id] :as recipient} :- dispatch-subs/PartyEmailRecipient]
  [:li.flex.flex-wrap.items-center
   [:span.mr-2
    (cc/checkbox {:id         (str party-entity-id)
                  :label-text (party-recipient-label lang recipient)
                  :checked    (contains? mail-recipients party-entity-id)
                  :on-change  #(>evt [::dispatch-subs/set-party-mail-recipient-ids
                                      ((if % conj disj) mail-recipients party-entity-id)])})]
   [:span.text-gray-600.text-xs (format-party-recipient-roles ctx recipient)]])


(defn party-recipient-list [tr mail-recipients mail-recipient-candidates]
  (let [ctx (util/mmap {:lang (<sub [:language])} tr)]
    [:div
     [:h4 (tr [:dispatching/send-mail-parties])]
     [:ul
      (->> mail-recipient-candidates
           (map (fn [{:keys [party-entity-id] :as recipient}]
                  ^{:key party-entity-id} [party-recipient ctx mail-recipients recipient])))]]))


(defn add-recipient-block [tr additional-recipients]
  (let [new-recipient (<sub [::dispatch-subs/new-additional-mail-recipient])
        invalid?      (<sub [::dispatch-subs/new-additional-mail-recipient-invalid?])]
    (when new-recipient
      [:div.bg-light-gray.border-dashed.border-2.border-ui-gray.mb-2
       [:div
        {:class "p-2 flex bg-white"}
        [:div.flex-1.pr-4
         (cc/text-input {:id          "input-email-new-contact"
                         :label-text  (tr [:party/email])
                         :light       true
                         :placeholder (tr [:placeholder/email])
                         :invalid     (<sub [::dispatch-subs/new-additional-mail-recipient-field-invalid? :email])
                         :on-change   #(>evt [::dispatch-subs/set-new-additional-mail-recipient {:email (futil/tval %)}])})]
        [:div.flex-1.text-right.pt-4
         (cc/button {:kind          :ghost
                     :icon-position :right
                     :size          :small
                     :icon          i/remove
                     :on-click      #(>evt [::dispatch-subs/set-new-additional-mail-recipient nil])}
                    (tr [:cancel]))
         (cc/button {:kind          :primary
                     :icon-position :right
                     :size          :small
                     :icon          i/plus
                     :on-click      (fn []
                                      (>evt [::dispatch-subs/set-additional-mail-recipients (conj additional-recipients new-recipient)])
                                      (>evt [::dispatch-subs/set-new-additional-mail-recipient nil]))
                     :disabled      invalid?}
                    (tr [:add]))]]])))


(defn additional-recipient-list [tr additional-recipients]
  (let [adding? (<sub [::dispatch-subs/adding-new-additional-mail-recipient?])]
    [:div
     [:h4 (tr [:dispatching/send-mail-additional-recipients])]
     [:ul
      (if (empty? additional-recipients)
        [:p (tr [:dispatching/send-mail-no-additional-recipients])]
        (futil/for-frag [recipient additional-recipients]
          [:li.flex.flex-wrap.items-center.justify-between
           [:span.mr-2 (:email recipient)]
           (cc/button {:size     :small
                       :kind     :ghost
                       :icon     i/trash
                       :on-click #(>evt [::dispatch-subs/set-additional-mail-recipients (disj additional-recipients recipient)])}
                      (tr [:delete]))]))
      [add-recipient-block tr additional-recipients]]
     (cc/button {:kind     :primary
                 :size     :small
                 :icon     i/plus
                 :disabled adding?
                 :on-click #(>evt [::dispatch-subs/set-new-additional-mail-recipient {:email ""}])}
                (tr [:dispatching/add-additional-email]))]))


(defn additional-notes [tr booking]
  (let [add-notes (<sub [::dispatch-subs/add-mail-notes])
        notes     (<sub [::dispatch-subs/mail-notes booking])]
    [:div
     (cc/checkbox
       {:id         "add-mail-notes"
        :label-text (tr [:dispatching/add-notes])
        :checked    add-notes
        :on-change  #(>evt [::dispatch-subs/toggle-add-mail-notes])})
     (when add-notes
       (ui/text-area {:id          "dispatch-notes"
                      :label-text  (tr [:dispatching/notes])
                      :placeholder (tr [:booking/notes-placeholder])
                      :value       notes
                      :on-change   (fn [e]
                                     (rf/dispatch-sync [::dispatch-subs/set-mail-notes
                                                        (-> e futil/tval)]))}))]))


(defn send-mail-button [tr id workspace booking]
  (let [loading?                   (<sub [::dispatch-subs/loading-workspace?])
        creator                    (:entity/creator booking)
        party-recipient-candidates (<sub [::dispatch-subs/party-recipient-candidates workspace creator])
        party-recipients           (<sub [::dispatch-subs/party-mail-recipient-ids party-recipient-candidates])
        additional-recipients      (<sub [::dispatch-subs/additional-mail-recipients])
        add-notes                  (<sub [::dispatch-subs/add-mail-notes])
        notes                      (<sub [::dispatch-subs/mail-notes booking])]
    [ui/modal-confirmation-button
     {:tr                  tr
      :id                  "send-mail"
      :modal-title         (tr [:dispatching/send-mail-confirm])
      :header-class        "w-5-6"
      :body-class          "w-5-6"
      :yes-disabled?       (or loading?
                               (and (empty? party-recipients)
                                    (empty? additional-recipients)))
      :trigger-button-opts {:kind       :primary
                            :icon       i/envelope
                            :class-name "py-2 min-h-0 uppercase flex self-stretch
                                                items-center justify-between pl-3 pr-10 border-0
                                                hover:border-0 text-xs font-semibold text-white
                                                no-underline bg-gray-900 hover:bg-gray-800"}
      :trigger-button-text (tr [:dispatching/send-email])
      :on-modal-open       (fn []
                             (>evt [::dispatch-subs/reset-confirmation-mail])
                             (>evt [::dispatch-subs/fetch-parties (:workspace/id workspace)]))
      :on-yes-click        #(>evt [::dispatch-subs/send-mail party-recipients additional-recipients (when add-notes notes) id])}
     [:<>
      [:p (tr [:dispatching/send-mail-confirm-body])]
      (if loading?
        [:div.mb-2
         (cc/loading-spinner {:with-overlay false
                              :small        true})]
        [:div
         [party-recipient-list tr party-recipients party-recipient-candidates]
         [ui-sep/horizontal]
         [additional-recipient-list tr additional-recipients]
         [ui-sep/horizontal]
         [additional-notes tr booking]])]]))
